import React, { useState, useEffect } from 'react';
import './GalleryModal.css';

interface GalleryImage {
  id: string;
  url: string;
  name: string;
  type: 'asset' | 'character' | 'background' | 'scene';
}

interface GalleryModalProps {
  isOpen: boolean;
  onClose: () => void;
  images: GalleryImage[];
  onRename: (id: string, newName: string) => void;
  onDelete: (id: string) => void;
  onMoveToType: (id: string, newType: 'asset' | 'character' | 'background' | 'scene') => Promise<void>;
}

const GalleryModal: React.FC<GalleryModalProps> = ({
  isOpen,
  onClose,
  images,
  onRename,
  onDelete,
  onMoveToType,
}) => {
  const [activeTab, setActiveTab] = useState<'asset' | 'character' | 'background' | 'scene'>('character');
  const [selectedImage, setSelectedImage] = useState<GalleryImage | null>(null);
  const [draggedImage, setDraggedImage] = useState<GalleryImage | null>(null);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [editName, setEditName] = useState('');
  const [dragOverTab, setDragOverTab] = useState<string | null>(null);
  const [dragOverDelete, setDragOverDelete] = useState(false);
  const [isMoving, setIsMoving] = useState(false);

  const filteredImages = images.filter(img => img.type === activeTab);

  useEffect(() => {
    // Only auto-select when modal opens or tab changes, not on every images change
    if (isOpen && filteredImages.length > 0 && !selectedImage) {
      setSelectedImage(filteredImages[0]);
    }
    // If the selected image is no longer in the filtered list, clear selection
    if (selectedImage && !filteredImages.find(img => img.id === selectedImage.id)) {
      setSelectedImage(null);
    }
  }, [isOpen, activeTab]); // Removed filteredImages and selectedImage from deps

  useEffect(() => {
    if (!isOpen) {
      setSelectedImage(null);
      setEditingId(null);
    }
  }, [isOpen]);

  if (!isOpen) return null;

  const handleTabChange = (tab: 'asset' | 'character' | 'background' | 'scene') => {
    setActiveTab(tab);
    setSelectedImage(null);
    setEditingId(null);
  };

  const handleImageClick = (image: GalleryImage) => {
    setSelectedImage(image);
    setEditingId(null);
  };

  const handleStartEdit = (image: GalleryImage) => {
    setEditingId(image.id);
    setEditName(image.name);
  };

  const handleSaveEdit = () => {
    if (editingId && editName.trim()) {
      onRename(editingId, editName.trim());
      setEditingId(null);
    }
  };

  const handleCancelEdit = () => {
    setEditingId(null);
    setEditName('');
  };

  const handleDeleteImage = (image: GalleryImage) => {
    if (window.confirm(`Are you sure you want to delete "${image.name}"?`)) {
      onDelete(image.id);
      if (selectedImage?.id === image.id) {
        setSelectedImage(null);
      }
    }
  };

  const handleDragStart = (e: React.DragEvent, image: GalleryImage) => {
    console.log('Drag start:', image.name);
    setDraggedImage(image);
    e.dataTransfer.effectAllowed = 'move';
    e.dataTransfer.setData('text/plain', image.id);
  };

  const handleDragEnd = () => {
    console.log('Drag end');
    setDraggedImage(null);
    setDragOverTab(null);
    setDragOverDelete(false);
  };

  const handleDragOverTab = (e: React.DragEvent, tab: string) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
    setDragOverTab(tab);
  };

  const handleDragLeaveTab = () => {
    setDragOverTab(null);
  };

  const handleDropOnTab = async (e: React.DragEvent, tab: 'asset' | 'character' | 'background' | 'scene') => {
    e.preventDefault();
    if (draggedImage && draggedImage.type !== tab && !isMoving) {
      setIsMoving(true);
      try {
        await onMoveToType(draggedImage.id, tab);
        // Stay on current tab, clear selection
        setSelectedImage(null);
      } catch (error) {
        console.error('Failed to move image:', error);
      } finally {
        setIsMoving(false);
      }
    }
    setDragOverTab(null);
    setDraggedImage(null);
  };

  const handleDragOverDelete = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    e.dataTransfer.dropEffect = 'move';
    console.log('Drag over delete zone');
    setDragOverDelete(true);
  };

  const handleDragLeaveDelete = (e: React.DragEvent) => {
    console.log('Drag leave delete zone');
    // Only clear if we're actually leaving the delete zone, not a child element
    const rect = (e.currentTarget as HTMLElement).getBoundingClientRect();
    const x = e.clientX;
    const y = e.clientY;
    
    if (x < rect.left || x >= rect.right || y < rect.top || y >= rect.bottom) {
      console.log('Actually leaving delete zone');
      setDragOverDelete(false);
    }
  };

  const handleDropOnDelete = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    console.log('Drop on delete zone', draggedImage);
    if (draggedImage) {
      console.log('Deleting image:', draggedImage.id);
      if (window.confirm(`Are you sure you want to delete "${draggedImage.name}"?`)) {
        onDelete(draggedImage.id);
        setSelectedImage(null);
      }
    }
    setDragOverDelete(false);
    setDraggedImage(null);
  };

  const tabs = [
    { id: 'character', label: 'Characters', icon: '🦊' },
    { id: 'asset', label: 'Assets', icon: '🎨' },
    { id: 'background', label: 'Backgrounds', icon: '🏞️' },
    { id: 'scene', label: 'Scenes', icon: '🎬' },
  ];

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="gallery-modal-content" onClick={(e) => e.stopPropagation()}>
        <div className="gallery-header">
          <h2>🖼️ Gallery</h2>
          <button className="close-button" onClick={onClose}>✕</button>
        </div>

        {isMoving && (
          <div className="moving-indicator">
            <div className="spinner"></div>
            <span>Moving image...</span>
          </div>
        )}

        <div className="gallery-body">
          {/* Left Sidebar - Tabs */}
          <div className="gallery-sidebar">
            <div className="gallery-tabs">
              {tabs.map(tab => (
                <button
                  key={tab.id}
                  className={`gallery-tab ${activeTab === tab.id ? 'active' : ''} ${dragOverTab === tab.id ? 'drag-over' : ''}`}
                  onClick={() => handleTabChange(tab.id as any)}
                  onDragOver={(e) => handleDragOverTab(e, tab.id)}
                  onDragLeave={handleDragLeaveTab}
                  onDrop={(e) => handleDropOnTab(e, tab.id as any)}
                >
                  <span className="tab-icon">{tab.icon}</span>
                  <span className="tab-label">{tab.label}</span>
                  <span className="tab-count">{images.filter(img => img.type === tab.id).length}</span>
                </button>
              ))}
            </div>

            {/* Delete Zone */}
            <div
              className={`gallery-delete-zone ${dragOverDelete ? 'drag-over' : ''}`}
              onDragEnter={(e) => {
                e.preventDefault();
                console.log('Drag ENTER delete zone');
                setDragOverDelete(true);
              }}
              onDragOver={(e) => {
                e.preventDefault();
                e.dataTransfer.dropEffect = 'move';
              }}
              onDragLeave={(e) => {
                const rect = (e.currentTarget as HTMLElement).getBoundingClientRect();
                if (e.clientX < rect.left || e.clientX >= rect.right || 
                    e.clientY < rect.top || e.clientY >= rect.bottom) {
                  console.log('Drag LEAVE delete zone');
                  setDragOverDelete(false);
                }
              }}
              onDrop={(e) => {
                e.preventDefault();
                console.log('DROP on delete zone!', draggedImage);
                if (draggedImage) {
                  if (window.confirm(`Delete "${draggedImage.name}"?`)) {
                    onDelete(draggedImage.id);
                    setSelectedImage(null);
                  }
                }
                setDragOverDelete(false);
                setDraggedImage(null);
              }}
            >
              <div style={{ pointerEvents: 'none', display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '8px' }}>
                <span className="delete-icon">🗑️</span>
                <span className="delete-label">Drop to Delete</span>
              </div>
            </div>
          </div>

          {/* Middle Section - Image Grid */}
          <div className="gallery-grid-section">
            <div className="gallery-grid-header">
              <h3>{tabs.find(t => t.id === activeTab)?.label}</h3>
              <span className="image-count">{filteredImages.length} items</span>
            </div>

            <div className="gallery-image-grid">
              {filteredImages.length === 0 ? (
                <div className="empty-state">
                  <p>No {activeTab}s yet</p>
                  <span>Generate some images to see them here</span>
                </div>
              ) : (
                filteredImages.map(image => (
                  <div
                    key={image.id}
                    className={`gallery-image-item ${selectedImage?.id === image.id ? 'selected' : ''}`}
                    onClick={() => handleImageClick(image)}
                    draggable={true}
                    onDragStart={(e) => handleDragStart(e, image)}
                    onDragEnd={handleDragEnd}
                  >
                    <div className="image-thumbnail">
                      <img src={image.url} alt={image.name} draggable={false} />
                    </div>
                    {editingId === image.id ? (
                      <div className="image-name-edit" onClick={(e) => e.stopPropagation()}>
                        <input
                          type="text"
                          value={editName}
                          onChange={(e) => setEditName(e.target.value)}
                          onKeyDown={(e) => {
                            if (e.key === 'Enter') handleSaveEdit();
                            if (e.key === 'Escape') handleCancelEdit();
                          }}
                          autoFocus
                        />
                        <div className="edit-buttons">
                          <button className="save-btn" onClick={handleSaveEdit}>✓</button>
                          <button className="cancel-btn" onClick={handleCancelEdit}>✕</button>
                        </div>
                      </div>
                    ) : (
                      <div className="image-name">{image.name}</div>
                    )}
                  </div>
                ))
              )}
            </div>
          </div>

          {/* Right Section - Preview */}
          <div className="gallery-preview-section">
            {selectedImage ? (
              <>
                <div className="preview-header">
                  <h3>Preview</h3>
                  <div className="preview-actions">
                    <button
                      className="action-btn rename-btn"
                      onClick={() => handleStartEdit(selectedImage)}
                      title="Rename"
                    >
                      ✏️
                    </button>
                    <button
                      className="action-btn delete-btn"
                      onClick={() => handleDeleteImage(selectedImage)}
                      title="Delete"
                    >
                      🗑️
                    </button>
                  </div>
                </div>

                <div className="preview-image-container">
                  <img src={selectedImage.url} alt={selectedImage.name} />
                </div>

                <div className="preview-details">
                  <div className="detail-row">
                    <span className="detail-label">Name:</span>
                    <span className="detail-value">{selectedImage.name}</span>
                  </div>
                  <div className="detail-row">
                    <span className="detail-label">Type:</span>
                    <span className="detail-value">{selectedImage.type}</span>
                  </div>
                  <div className="detail-row">
                    <span className="detail-label">ID:</span>
                    <span className="detail-value detail-id">{selectedImage.id}</span>
                  </div>
                </div>

                <div className="drag-hint">
                  <p>💡 <strong>Tip:</strong> Drag images to tabs on the left to change their type, or drag to the delete zone to remove them.</p>
                </div>
              </>
            ) : (
              <div className="preview-empty">
                <p>Select an image to preview</p>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default GalleryModal;
